// ═══════════════════════════════════════════════════════════════════════════
// Pay Rules Engine — BuildMyShifts
// Calculates pay for shift segments, handles sleep-in flat rates, on-call,
// premium/enhanced pay detection, overtime, and projected rota costs.
// ═══════════════════════════════════════════════════════════════════════════

import {
  SEGMENT_TYPES, PAY_TYPES, DEFAULT_PAY_RULES,
  getBlockWorkingHours, detectPayType, getPayMultiplier,
  isBankHoliday, isWeekend,
} from './shiftTypes';
import { timeToMinutes } from './schedulingEngine';

/**
 * Calculate pay for a single segment
 * Returns { hours, payType, multiplier, flatRate, cost, label }
 */
export function calculateSegmentPay(segment, dateStr, staffPayRate = 0, payRules = DEFAULT_PAY_RULES) {
  const result = {
    segmentId: segment.id,
    type: segment.type,
    startTime: segment.startTime,
    endTime: segment.endTime,
    hours: 0,
    payType: PAY_TYPES.STANDARD,
    multiplier: 1.0,
    flatRate: null,
    cost: 0,
    label: 'Standard',
  };

  // Break segments have zero cost
  if (segment.type === SEGMENT_TYPES.BREAK) {
    result.label = 'Break (unpaid)';
    return result;
  }

  // Calculate hours
  let startMins = timeToMinutes(segment.startTime);
  let endMins = timeToMinutes(segment.endTime);
  if (endMins <= startMins) endMins += 24 * 60;
  const hours = (endMins - startMins) / 60;
  result.hours = Math.round(hours * 100) / 100;

  // Sleep-in: flat rate
  if (segment.type === SEGMENT_TYPES.SLEEP_IN) {
    result.payType = PAY_TYPES.SLEEP_IN_FLAT;
    result.flatRate = segment.flatRate ?? payRules.sleepInFlatRate ?? 50;
    result.cost = result.flatRate;
    result.label = `Sleep-In (flat ${payRules.currencySymbol || '£'}${result.flatRate})`;
    result.multiplier = 0; // Flat rate, not multiplied
    return result;
  }

  // On-call: standby rate (per hour)
  if (segment.type === SEGMENT_TYPES.ON_CALL) {
    result.payType = PAY_TYPES.ON_CALL_STANDBY;
    const standbyRate = payRules.onCallStandbyRate ?? 5;
    result.cost = Math.round(hours * standbyRate * 100) / 100;
    result.multiplier = standbyRate / (staffPayRate || 1);
    result.label = `On-Call Standby (${payRules.currencySymbol || '£'}${standbyRate}/hr)`;
    return result;
  }

  // Standard segment — detect enhancements
  const detectedPay = segment.payType !== PAY_TYPES.STANDARD
    ? segment.payType
    : detectPayType(segment, dateStr, payRules);

  result.payType = detectedPay;
  result.multiplier = getPayMultiplier(detectedPay, payRules);

  // Label
  switch (detectedPay) {
    case PAY_TYPES.BANK_HOLIDAY: result.label = 'Bank Holiday'; break;
    case PAY_TYPES.WEEKEND: result.label = 'Weekend'; break;
    case PAY_TYPES.NIGHT_PREMIUM: result.label = 'Night Premium'; break;
    case PAY_TYPES.OVERTIME: result.label = 'Overtime'; break;
    default: result.label = 'Standard';
  }

  result.cost = Math.round(hours * staffPayRate * result.multiplier * 100) / 100;
  return result;
}

/**
 * Calculate pay for an entire shift block (all segments)
 * Returns { segments: [...payBreakdown], totalCost, totalHours, breakdown }
 */
export function calculateBlockPay(shift, staffPayRate = 0, payRules = DEFAULT_PAY_RULES) {
  const segments = shift.segments || [{
    id: 'legacy',
    type: SEGMENT_TYPES.STANDARD,
    startTime: shift.startTime,
    endTime: shift.endTime,
    payType: PAY_TYPES.STANDARD,
    flatRate: null,
    notes: '',
  }];

  const dateStr = shift.date;
  const segmentPays = segments.map(seg => calculateSegmentPay(seg, dateStr, staffPayRate, payRules));

  const totalCost = segmentPays.reduce((sum, s) => sum + s.cost, 0);
  const totalHours = segmentPays.reduce((sum, s) => sum + s.hours, 0);

  // Group by pay type for breakdown
  const breakdown = {};
  for (const sp of segmentPays) {
    if (!breakdown[sp.payType]) {
      breakdown[sp.payType] = { hours: 0, cost: 0, label: sp.label };
    }
    breakdown[sp.payType].hours += sp.hours;
    breakdown[sp.payType].cost += sp.cost;
  }

  return {
    segments: segmentPays,
    totalCost: Math.round(totalCost * 100) / 100,
    totalHours: Math.round(totalHours * 100) / 100,
    breakdown,
  };
}

/**
 * Check if a shift segment qualifies for overtime based on daily/weekly thresholds
 */
export function calculateOvertimeHours(staffId, date, existingShifts, newHours, payRules = DEFAULT_PAY_RULES) {
  // Daily overtime
  const dailyShifts = existingShifts.filter(s =>
    s.assignedStaffId === staffId && s.date === date && s.status !== 'cancelled'
  );
  const existingDailyHours = dailyShifts.reduce((sum, s) => {
    const segs = s.segments || [{ startTime: s.startTime, endTime: s.endTime, type: SEGMENT_TYPES.STANDARD }];
    return sum + getBlockWorkingHours(segs, false);
  }, 0);

  const dailyThreshold = payRules.dailyOvertimeThresholdHours || 8;
  const dailyTotal = existingDailyHours + newHours;
  const dailyOvertime = Math.max(0, dailyTotal - dailyThreshold);   const dailyStandard = newHours - dailyOvertime;

  // Weekly overtime
  const shiftDate = new Date(date);
  const day = shiftDate.getDay();
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(shiftDate);
  monday.setDate(monday.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(sunday.getDate() + 6);

  const weeklyShifts = existingShifts.filter(s => {
    if (s.assignedStaffId !== staffId) return false;
    if (s.status === 'cancelled') return false;
    const d = new Date(s.date);
    return d >= monday && d <= sunday;
  });
  const existingWeeklyHours = weeklyShifts.reduce((sum, s) => {
    const segs = s.segments || [{ startTime: s.startTime, endTime: s.endTime, type: SEGMENT_TYPES.STANDARD }];
    return sum + getBlockWorkingHours(segs, false);
  }, 0);

  const weeklyThreshold = payRules.weeklyOvertimeThresholdHours || 40;
  const weeklyTotal = existingWeeklyHours + newHours;
  const weeklyOvertime = Math.max(0, weeklyTotal - weeklyThreshold);

  return {
    dailyOvertime: Math.round(Math.max(dailyOvertime, 0) * 100) / 100,
    weeklyOvertime: Math.round(Math.max(weeklyOvertime, 0) * 100) / 100,
    totalOvertime: Math.round(Math.max(dailyOvertime, weeklyOvertime) * 100) / 100,
    dailyTotal: Math.round(dailyTotal * 100) / 100,
    weeklyTotal: Math.round(weeklyTotal * 100) / 100,
    dailyStandard: Math.round(Math.max(dailyStandard, 0) * 100) / 100,
  };
}

/**
 * Calculate on-call callout cost
 * Called when a worker on an on-call shift is called out
 */
export function calculateCalloutCost(calloutStartTime, calloutEndTime, staffPayRate, payRules = DEFAULT_PAY_RULES) {
  let startMins = timeToMinutes(calloutStartTime);
  let endMins = timeToMinutes(calloutEndTime);
  if (endMins <= startMins) endMins += 24 * 60;
  const hours = (endMins - startMins) / 60;
  const rate = payRules.onCallCalloutRate || staffPayRate;

  return {
    hours: Math.round(hours * 100) / 100,
    rate,
    cost: Math.round(hours * rate * 100) / 100,
    payType: PAY_TYPES.ON_CALL_CALLOUT,
  };
}

/**
 * Project total rota cost before publishing
 * Iterates all shifts in the week, calculates pay for each, sums up
 */
export function projectRotaCost(shifts, staffMap, payRules = DEFAULT_PAY_RULES) {
  let totalCost = 0;
  let totalHours = 0;
  let sleepInFees = 0;
  let onCallCost = 0;
  let premiumCost = 0;
  let standardCost = 0;
  let overtimeCost = 0;

  const staffCosts = {};

  for (const shift of shifts) {
    if (shift.status === 'cancelled') continue;
    const staffPayRate = staffMap[shift.assignedStaffId]?.payRate || 0;
    const blockPay = calculateBlockPay(shift, staffPayRate, payRules);

    totalCost += blockPay.totalCost;
    totalHours += blockPay.totalHours;

    // Categorise
    for (const [payType, data] of Object.entries(blockPay.breakdown)) {
      switch (payType) {
        case PAY_TYPES.SLEEP_IN_FLAT:
        case PAY_TYPES.SLEEP_IN_DISTURBANCE:
          sleepInFees += data.cost;
          break;
        case PAY_TYPES.ON_CALL_STANDBY:
        case PAY_TYPES.ON_CALL_CALLOUT:
          onCallCost += data.cost;
          break;
        case PAY_TYPES.OVERTIME:
          overtimeCost += data.cost;
          break;
        case PAY_TYPES.NIGHT_PREMIUM:
        case PAY_TYPES.WEEKEND:
        case PAY_TYPES.BANK_HOLIDAY:
          premiumCost += data.cost;
          break;
        default:
          standardCost += data.cost;
      }
    }

    // Per-staff breakdown
    if (shift.assignedStaffId) {
      if (!staffCosts[shift.assignedStaffId]) {
        staffCosts[shift.assignedStaffId] = {
          name: shift.assignedStaffName,
          hours: 0,
          cost: 0,
        };
      }
      staffCosts[shift.assignedStaffId].hours += blockPay.totalHours;
      staffCosts[shift.assignedStaffId].cost += blockPay.totalCost;
    }
  }

  return {
    totalCost: Math.round(totalCost * 100) / 100,
    totalHours: Math.round(totalHours * 100) / 100,
    standardCost: Math.round(standardCost * 100) / 100,
    premiumCost: Math.round(premiumCost * 100) / 100,
    overtimeCost: Math.round(overtimeCost * 100) / 100,
    sleepInFees: Math.round(sleepInFees * 100) / 100,
    onCallCost: Math.round(onCallCost * 100) / 100,
    staffCosts,
    shiftCount: shifts.filter(s => s.status !== 'cancelled').length,
  };
}

/**
 * Generate payroll-ready data with full breakdown per record
 * Used by PayrollExport for enhanced exports
 */
export function generatePayrollBreakdown(timeRecords, shifts, staffMap, payRules = DEFAULT_PAY_RULES) {
  const enrichedRecords = [];

  for (const record of timeRecords) {
    const baseRecord = {
      ...record,
      standardHours: 0,
      overtimeHours: 0,
      nightPremiumHours: 0,
      weekendHours: 0,
      bankHolidayHours: 0,
      sleepInFee: 0,
      onCallHours: 0,
      onCallCalloutHours: 0,
      totalPay: 0,
    };

    // Find matching shift for this time record
    const matchingShift = shifts.find(s =>
      s.assignedStaffId === record.userId && s.date === record.date
    );

    const staffPayRate = staffMap[record.userId]?.payRate || 0;

    if (matchingShift && matchingShift.segments) {
      // Use segment-level calculation
      const blockPay = calculateBlockPay(matchingShift, staffPayRate, payRules);
      for (const [payType, data] of Object.entries(blockPay.breakdown)) {
        switch (payType) {
          case PAY_TYPES.STANDARD: baseRecord.standardHours += data.hours; break;
          case PAY_TYPES.OVERTIME: baseRecord.overtimeHours += data.hours; break;
          case PAY_TYPES.NIGHT_PREMIUM: baseRecord.nightPremiumHours += data.hours; break;
          case PAY_TYPES.WEEKEND: baseRecord.weekendHours += data.hours; break;
          case PAY_TYPES.BANK_HOLIDAY: baseRecord.bankHolidayHours += data.hours; break;
          case PAY_TYPES.SLEEP_IN_FLAT: baseRecord.sleepInFee += data.cost; break;
          case PAY_TYPES.ON_CALL_STANDBY: baseRecord.onCallHours += data.hours; break;
          case PAY_TYPES.ON_CALL_CALLOUT: baseRecord.onCallCalloutHours += data.hours; break;
          default: baseRecord.standardHours += data.hours;
        }
      }
      baseRecord.totalPay = blockPay.totalCost;
    } else {
      // Fallback: use time record duration with auto-detection
      const hours = (record.duration || 0) / 60;
      const dateStr = record.date;

      if (isBankHoliday(dateStr, payRules.customBankHolidays)) {
        baseRecord.bankHolidayHours = hours;
        baseRecord.totalPay = hours * staffPayRate * (payRules.bankHolidayMultiplier || 2.0);
      } else if (isWeekend(dateStr, payRules.weekendDays)) {
        baseRecord.weekendHours = hours;
        baseRecord.totalPay = hours * staffPayRate * (payRules.weekendMultiplier || 1.25);
      } else {
        baseRecord.standardHours = hours;
        baseRecord.totalPay = hours * staffPayRate;
      }
    }

    baseRecord.totalPay = Math.round(baseRecord.totalPay * 100) / 100;
    enrichedRecords.push(baseRecord);
  }

  return enrichedRecords;
}

const payRules = {
  calculateSegmentPay,
  calculateBlockPay,
  calculateOvertimeHours,
  calculateCalloutCost,
  projectRotaCost,
  generatePayrollBreakdown,
};
export default payRules;
